// @flow
import {trigger as edapTrigger} from './edapWrapper';
import type {SearchParamsType} from '../reducers/searchParams';
import type {ListingShape} from '../components/Hit/Hit';
import type {PriceSummaryShape} from '../components/Price/Price';

import {logExperiment} from '@homeaway/abacus-client-utils';
import {VRBO_LANDING_PROPERTY_HERO_IMAGES_SELECTION} from '../abacus/experiments';

export const SEARCH_RESULT_SET_PRESENTED = 'search_result_set.presented';
export const SERP_HIT_PRESENTED = 'serp_hit.presented';
export const VIRTUAL_TOUR_BADGE_PRESENTED = 'virtualtour_badge.presented';
export const SERP_HIT_MAB_PRESENTED = 'multi_armed_bandit.presented';
export const SERP_RECENTLY_VIEWED_CAROUSEL_PRESENTED =
    'content_module.presented';
export const SERP_RECENTLY_VIEWED_CAROUSEL_CARD_PRESENTED =
    'content_tile.presented';
export const SERP_RECENTLY_VIEWED_CAROUSEL_CARD_SELECTED =
    'content_tile.selected';

export const triggerSearchResultSetPresentedEdapEvent = (
    inputParams: SearchParamsType,
    queryUUID: string
) => {
    const {
        adultsCount,
        childrenCount,
        petIncluded,
        arrival,
        departure
    } = inputParams;

    const data = {
        action_location: 'search',
        adult_count: adultsCount ? `${adultsCount}` : null,
        child_count: childrenCount ? `${childrenCount}` : null,
        with_pets: String(petIncluded),
        arrival,
        departure,
        search_id: queryUUID
    };

    edapTrigger(SEARCH_RESULT_SET_PRESENTED, data);
};

export const getEdapEventJson = (priceSummary: ?PriceSummaryShape) => {
    let defaultNullObject = {price_accurate: null, total_price: null};
    if (!priceSummary) {
        return defaultNullObject;
    }
    try {
        return JSON.parse(priceSummary.edapEventJson);
    } catch (e) {
        return defaultNullObject;
    }
};

let serpHitPresentedEdapOccured = new Set();
export const triggerSerpHitPresentedEdapEvent = (listing: ListingShape) => {
    const {priceSummary, listingId, virtualTourBadge} = listing;
    if (serpHitPresentedEdapOccured.has(listingId)) {
        return;
    }

    const {
        // eslint-disable-next-line camelcase
        price_accurate,
        // eslint-disable-next-line camelcase
        total_price: total_price_available
    } = getEdapEventJson(priceSummary);

    const data = {
        action_location: 'search',
        listing_id: listingId,
        price_accurate,
        total_price_available
    };

    if (virtualTourBadge) {
        const virtualTourData = {
            action_location: 'search',
            tourid: '00000000-0000-0000-0000-000000000000'
        };
        edapTrigger(VIRTUAL_TOUR_BADGE_PRESENTED, virtualTourData);
    }

    edapTrigger(SERP_HIT_PRESENTED, data);
    serpHitPresentedEdapOccured.add(listingId);
};

let serpHitMabPresentedEdapOccured = new Set();
export const triggerSerpHitMabPresentedEdapEvent = (
    listing: ListingShape,
    havId: string,
    siteName: string,
    deviceType: string
) => {
    const {listingId, images} = listing;
    const mab = images && images.length && images[0].mab;
    const arm = mab && mab.arm;

    if (mab) {
        logExperiment(VRBO_LANDING_PROPERTY_HERO_IMAGES_SELECTION);
    }

    if (serpHitMabPresentedEdapOccured.has(listingId) || !arm) {
        return;
    }

    const data = {
        action_location: 'search',
        bandit_id: mab && mab.banditId,
        campaign_id: mab && mab.campaignId,
        cached: mab && mab.cached,
        visitor_id: havId,
        arm_id: {
            categoryName: arm && arm.categoryName,
            level: arm && arm.level
        },
        context: {monikerName: siteName, device: deviceType},
        payload_id: mab && mab.payloadId
    };

    edapTrigger(SERP_HIT_MAB_PRESENTED, data);
    serpHitMabPresentedEdapOccured.add(listingId);
};

export const triggerPresentedSerpRecentlyViewedCarouselEvent = () => {
    const data = {
        action_location: 'recently_viewed_carousel_module',
        position_fired: 'top'
    };

    edapTrigger(SERP_RECENTLY_VIEWED_CAROUSEL_PRESENTED, data);
};

export const triggerPresentedSerpCarouselCardEvent = (listingId: string) => {
    const data = {
        action_location: 'recently_viewed_carousel_module',
        event_action: 'recently_viewed_carousel_card_presented',
        listingid: listingId
    };

    edapTrigger(SERP_RECENTLY_VIEWED_CAROUSEL_CARD_PRESENTED, data);
};

export const triggerSelectedSerpCarouselCardEvent = (
    checkIn: string,
    listingId: string
) => {
    const data = {
        action_location: 'recently_viewed_carousel_module',
        event_action: 'recently_viewed_carousel_card_selected',
        event_refinements: `checkindate:${checkIn}`,
        listingid: listingId
    };

    edapTrigger(SERP_RECENTLY_VIEWED_CAROUSEL_CARD_SELECTED, data);
};
